import { useMemo, type ReactElement } from 'react';
import { useIntl } from 'react-intl';
import { useAbility } from '../features/auth/abilityContext';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import HistoryIcon from '@mui/icons-material/History';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import CircularProgress from '@mui/material/CircularProgress';
import DeferredActionsCell from './DeferredActionsCell';
import {
    GridActionsCellItem,
    type GridActionsCellItemProps,
    type GridColDef,
    type GridValidRowModel
} from '../lib/dataGrid';
import {
    TooltipActionItem,
    type TooltipActionItemProps
} from './TooltipActionItem';

type ActionItem = ReactElement<GridActionsCellItemProps>;

/**
 * Factories for the row-action buttons that repeat across every grid's
 * `actions` column (history / edit / delete / open, plus the inline-edit
 * save/cancel pair). Each returns one action element with its own tooltip; a
 * grid's `getActions` composes the array it needs.
 */
export function useGridRowActions() {
    const intl = useIntl();
    const ability = useAbility();

    function make(
        key: string,
        icon: ReactElement,
        labelId: string,
        onClick: () => void,
        props: Partial<TooltipActionItemProps> = {}
    ): ActionItem {
        return (
            <TooltipActionItem
                key={key}
                icon={icon}
                label={intl.formatMessage({ id: labelId })}
                color="inherit"
                onClick={onClick}
                {...props}
            />
        );
    }

    // An action only one grid has. Anything that repeats across grids gets a
    // named factory below instead.
    function custom(
        key: string,
        icon: ReactElement,
        labelId: string,
        onClick: () => void,
        props: Partial<TooltipActionItemProps> = {}
    ): ActionItem {
        return make(key, icon, labelId, onClick, props);
    }

    /**
     * An entry of the row's ⋮ menu rather than an icon of its own — for the
     * actions a grid reaches for less often, where a row of icons would run
     * out of width. Labelled in words, so no tooltip.
     */
    function menu(
        key: string,
        icon: ReactElement,
        labelId: string,
        onClick: () => void,
        { disabled = false }: { disabled?: boolean } = {}
    ): ActionItem {
        return (
            <GridActionsCellItem
                key={key}
                icon={icon}
                label={intl.formatMessage({ id: labelId })}
                onClick={onClick}
                disabled={disabled}
                showInMenu
            />
        );
    }

    /**
     * The Verlauf entry of the ⋮ menu — or nothing, under the same right as
     * {@link history}.
     */
    function historyMenu(
        onClick: () => void,
        labelId = 'common.history'
    ): ActionItem[] {
        if (!ability.can('access', 'RecordHistory')) {
            return [];
        }
        return [menu('history', <HistoryIcon />, labelId, onClick)];
    }

    /**
     * The Verlauf action — or nothing at all. A version list is
     * `access RecordHistory` (Administrator, Projektmanagement,
     * Projektassistenz); reading a version is a plain read as far as the server
     * is concerned, so this hides work that is not yours, not anything secret.
     *
     * Returns a LIST so the caller spreads it: an element that rendered null
     * would still take a slot, and the cell's keyboard handling counts its
     * children.
     */
    function history(
        onClick: () => void,
        props: Partial<TooltipActionItemProps> = {}
    ): ActionItem[] {
        if (!ability.can('access', 'RecordHistory')) {
            return [];
        }
        return [
            make('history', <HistoryIcon />, 'common.history', onClick, props)
        ];
    }

    function edit(onClick: () => void): ActionItem {
        return make('edit', <EditIcon />, 'common.edit', onClick);
    }

    function open(onClick: () => void): ActionItem {
        return make('open', <EditIcon />, 'common.open', onClick);
    }

    // Opening a record its reader may not change: an eye, not a pencil.
    function view(onClick: () => void): ActionItem {
        return make('view', <VisibilityOutlinedIcon />, 'common.open', onClick);
    }

    function excabookExport(onClick: () => void, busy = false): ActionItem {
        return make(
            'excabook-export',
            busy ? (
                <CircularProgress size={18} color="inherit" />
            ) : (
                <DownloadIcon />
            ),
            'excabook.export',
            onClick,
            { disabled: busy }
        );
    }

    function remove(onClick: () => void): ActionItem {
        return make('delete', <DeleteIcon />, 'common.delete', onClick);
    }

    function save(onClick: () => void): ActionItem {
        return make('save', <SaveIcon />, 'common.save', onClick, {
            color: 'primary'
        });
    }

    function cancel(onClick: () => void): ActionItem {
        return make('cancel', <CloseIcon />, 'common.cancel', onClick);
    }

    // Stable as long as the language is: a grid that builds its columns in a
    // `useMemo` can name this in the deps without rebuilding them every render.
    return useMemo(
        () => ({
            custom,
            menu,
            history,
            historyMenu,
            edit,
            open,
            view,
            remove,
            save,
            cancel,
            excabookExport
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [intl, ability]
    );
}

/**
 * The actions column every record grid ends on: Verlauf for whoever may see a
 * version list, plus Bearbeiten and Löschen for a user who may edit this
 * project's records — or Öffnen for one who may only read them, which opens
 * the same dialog as a read-only sheet. A grid with an action of its own — the Befund tree's
 * PDF, the Zeiterfassung's inline Save/Abbrechen — still composes its own array
 * from the factories above.
 */
export function useRecordActionsColumn<Row extends GridValidRowModel>({
    canEdit,
    onEdit,
    onDelete,
    onHistory
}: {
    canEdit: boolean;
    onEdit: (row: Row) => void;
    onDelete: (row: Row) => void;
    onHistory: (row: Row) => void;
}): GridColDef<Row> {
    const rowAction = useGridRowActions();
    return {
        field: 'actions',
        type: 'actions',
        headerName: '',
        width: 130,
        /**
         * Built only while the grid stands still: these buttons are the most
         * expensive thing in a scrolling row (see DeferredActionsCell).
         */
        renderCell: (params) => <DeferredActionsCell {...params} />,
        getActions: ({ row }) => {
            const actions = [...rowAction.history(() => onHistory(row))];
            if (canEdit) {
                actions.push(
                    rowAction.edit(() => onEdit(row)),
                    rowAction.remove(() => onDelete(row))
                );
            } else {
                actions.push(rowAction.view(() => onEdit(row)));
            }
            return actions;
        }
    };
}
